// Template Service - Gestión completa de plantillas de email
class TemplateService {
  constructor() {
    this.storageKey = 'emailTemplates'
    this.predefinedTemplates = this.getPredefinedTemplates()
  }

  // =================== GESTIÓN DE PLANTILLAS ===================

  /**
   * Obtiene todas las plantillas (guardadas + predefinidas)
   */
  getAllTemplates() {
    const savedTemplates = this.getSavedTemplates()
    return {
      saved: savedTemplates,
      predefined: this.predefinedTemplates
    }
  }

  /**
   * Obtiene plantillas guardadas por el usuario
   */
  getSavedTemplates() {
    try {
      const templates = localStorage.getItem(this.storageKey)
      return templates ? JSON.parse(templates) : []
    } catch (error) {
      console.error('Error al cargar plantillas:', error)
      return []
    }
  }

  /**
   * Guarda una nueva plantilla
   */
  saveTemplate(template) {
    try {
      const templates = this.getSavedTemplates()
      const newTemplate = {
        id: this.generateId(),
        name: template.name,
        description: template.description || '',
        thumbnail: template.thumbnail || this.generateThumbnail(template.content),
        content: template.content,
        category: template.category || 'custom',
        tags: template.tags || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isCustom: true
      }

      templates.push(newTemplate)
      localStorage.setItem(this.storageKey, JSON.stringify(templates))
      
      return {
        success: true,
        template: newTemplate,
        message: 'Plantilla guardada exitosamente'
      }
    } catch (error) {
      console.error('Error al guardar plantilla:', error)
      return {
        success: false,
        message: 'Error al guardar la plantilla'
      }
    }
  }

  /**
   * Actualiza una plantilla existente
   */
  updateTemplate(templateId, updates) {
    try {
      const templates = this.getSavedTemplates()
      const templateIndex = templates.findIndex(t => t.id === templateId)
      
      if (templateIndex === -1) {
        return {
          success: false,
          message: 'Plantilla no encontrada'
        }
      }

      templates[templateIndex] = {
        ...templates[templateIndex],
        ...updates,
        updatedAt: new Date().toISOString()
      }

      localStorage.setItem(this.storageKey, JSON.stringify(templates))
      
      return {
        success: true,
        template: templates[templateIndex],
        message: 'Plantilla actualizada exitosamente'
      }
    } catch (error) {
      console.error('Error al actualizar plantilla:', error)
      return {
        success: false,
        message: 'Error al actualizar la plantilla'
      }
    }
  }

  /**
   * Elimina una plantilla
   */
  deleteTemplate(templateId) {
    try {
      const templates = this.getSavedTemplates()
      const filteredTemplates = templates.filter(t => t.id !== templateId)
      
      localStorage.setItem(this.storageKey, JSON.stringify(filteredTemplates))
      
      return {
        success: true,
        message: 'Plantilla eliminada exitosamente'
      }
    } catch (error) {
      console.error('Error al eliminar plantilla:', error)
      return {
        success: false,
        message: 'Error al eliminar la plantilla'
      }
    }
  }

  /**
   * Obtiene una plantilla por ID
   */
  getTemplateById(templateId) {
    const savedTemplates = this.getSavedTemplates()
    let template = savedTemplates.find(t => t.id === templateId)
    
    if (!template) {
      template = this.predefinedTemplates.find(t => t.id === templateId)
    }
    
    return template || null
  }

  // =================== PLANTILLAS PREDEFINIDAS ===================

  getPredefinedTemplates() {
    return [
      {
        id: 'newsletter-modern',
        name: 'Newsletter Moderno',
        description: 'Plantilla moderna y limpia para newsletters',
        thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDIwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjRjhGOUZBIi8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjE2MCIgaGVpZ2h0PSI2MCIgZmlsbD0iIzAwN0JGRiIvPgo8cmVjdCB4PSIyMCIgeT0iMTAwIiB3aWR0aD0iNzUiIGhlaWdodD0iNDAiIGZpbGw9IiNFOUVDRUYiLz4KPHJlY3QgeD0iMTA1IiB5PSIxMDAiIHdpZHRoPSI3NSIgaGVpZ2h0PSI0MCIgZmlsbD0iI0U5RUNFRiIvPgo8L3N2Zz4K',
        category: 'newsletter',
        tags: ['moderno', 'limpio', 'newsletter'],
        content: [
          {
            id: 'header_1',
            type: 'TextBlock',
            content: '<h1 style="color: #ffffff; text-align: center; margin: 0;">Mi Newsletter</h1>',
            props: { fontSize: '28px', color: '#ffffff' }
          },
          {
            id: 'image_1',
            type: 'ImageBlock',
            content: 'https://via.placeholder.com/600x200/007bff/ffffff?text=Newsletter+Header',
            props: { width: '100%', alt: 'Newsletter Header' }
          },
          {
            id: 'text_1',
            type: 'TextBlock',
            content: '<h2>¡Hola!</h2><p>Bienvenido a nuestro newsletter mensual. Aquí encontrarás las últimas novedades y contenido exclusivo.</p>',
            props: { fontSize: '16px', color: '#2c3e50' }
          },
          {
            id: 'columns_1',
            type: 'ColumnBlock',
            content: [
              { content: '<h3>Noticia 1</h3><p>Contenido de la primera noticia...</p>' },
              { content: '<h3>Noticia 2</h3><p>Contenido de la segunda noticia...</p>' }
            ],
            props: { columns: 2, gap: '20px' }
          },
          {
            id: 'social_1',
            type: 'SocialBlock',
            content: '',
            props: {
              title: '¡Síguenos!',
              socialLinks: [
                { name: 'Facebook', icon: 'fab fa-facebook-f', url: '#', color: '#1877f2' },
                { name: 'Twitter', icon: 'fab fa-twitter', url: '#', color: '#1da1f2' },
                { name: 'Instagram', icon: 'fab fa-instagram', url: '#', color: '#e4405f' }
              ]
            }
          }
        ],
        isCustom: false
      },
      {
        id: 'promo-sale',
        name: 'Promoción de Ventas',
        description: 'Plantilla llamativa para promociones y ofertas especiales',
        thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDIwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjRkY2ODQ0Ii8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjE2MCIgaGVpZ2h0PSI2MCIgZmlsbD0iI0ZGRkZGRiIvPgo8cmVjdCB4PSI2MCIgeT0iMTIwIiB3aWR0aD0iODAiIGhlaWdodD0iNDAiIGZpbGw9IiMyOEE3NDUiLz4KPC9zdmc+Cg==',
        category: 'promocional',
        tags: ['venta', 'promoción', 'oferta'],
        content: [
          {
            id: 'text_1',
            type: 'TextBlock',
            content: '<h1 style="color: #ff6844; text-align: center; font-size: 36px; margin: 0;">¡OFERTA ESPECIAL!</h1>',
            props: { fontSize: '36px', color: '#ff6844' }
          },
          {
            id: 'text_2',
            type: 'TextBlock',
            content: '<h2 style="text-align: center; color: #2c3e50;">50% de Descuento</h2><p style="text-align: center; font-size: 18px;">En todos nuestros productos por tiempo limitado</p>',
            props: { fontSize: '18px', color: '#2c3e50' }
          },
          {
            id: 'image_1',
            type: 'ImageBlock',
            content: 'https://via.placeholder.com/600x300/ff6844/ffffff?text=Producto+en+Oferta',
            props: { width: '100%', alt: 'Producto en Oferta' }
          },
          {
            id: 'button_1',
            type: 'ButtonBlock',
            content: {
              text: 'COMPRAR AHORA',
              url: '#',
              styles: {
                backgroundColor: '#28a745',
                color: '#ffffff',
                align: 'center'
              }
            },
            props: {}
          },
          {
            id: 'text_3',
            type: 'TextBlock',
            content: '<p style="text-align: center; font-size: 14px; color: #6c757d;">*Oferta válida hasta el 31 de diciembre. No acumulable con otras promociones.</p>',
            props: { fontSize: '14px', color: '#6c757d' }
          }
        ],
        isCustom: false
      },
      {
        id: 'welcome-email',
        name: 'Email de Bienvenida',
        description: 'Plantilla cálida para dar la bienvenida a nuevos usuarios',
        thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDIwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjRjhGOUZBIi8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjE2MCIgaGVpZ2h0PSI0MCIgZmlsbD0iIzI4QTc0NSIvPgo8Y2lyY2xlIGN4PSIxMDAiIGN5PSIxMDAiIHI9IjMwIiBmaWxsPSIjRkZDMTA3Ii8+CjxyZWN0IHg9IjQwIiB5PSIxNjAiIHdpZHRoPSIxMjAiIGhlaWdodD0iMjAiIGZpbGw9IiNFOUVDRUYiLz4KPC9zdmc+Cg==',
        category: 'bienvenida',
        tags: ['bienvenida', 'onboarding', 'usuario nuevo'],
        content: [
          {
            id: 'text_1',
            type: 'TextBlock',
            content: '<h1 style="color: #28a745; text-align: center;">¡Bienvenido a nuestra comunidad!</h1>',
            props: { fontSize: '28px', color: '#28a745' }
          },
          {
            id: 'text_2',
            type: 'TextBlock',
            content: '<p>Hola <strong>{nombre}</strong>,</p><p>¡Estamos emocionados de tenerte con nosotros! Tu cuenta ha sido creada exitosamente y ya puedes comenzar a disfrutar de todos nuestros servicios.</p>',
            props: { fontSize: '16px', color: '#2c3e50' }
          },
          {
            id: 'button_1',
            type: 'ButtonBlock',
            content: {
              text: 'Completar Perfil',
              url: '#',
              styles: {
                backgroundColor: '#007bff',
                color: '#ffffff',
                align: 'center'
              }
            },
            props: {}
          },
          {
            id: 'text_3',
            type: 'TextBlock',
            content: '<h3>Próximos pasos:</h3><ul><li>Completa tu perfil</li><li>Explora nuestras funcionalidades</li><li>Únete a nuestra comunidad</li></ul>',
            props: { fontSize: '16px', color: '#2c3e50' }
          },
          {
            id: 'text_4',
            type: 'TextBlock',
            content: '<p>Si tienes alguna pregunta, no dudes en contactarnos. ¡Estamos aquí para ayudarte!</p><p>Saludos,<br>El equipo de [Nombre de la empresa]</p>',
            props: { fontSize: '16px', color: '#2c3e50' }
          }
        ],
        isCustom: false
      },
      {
        id: 'event-invitation',
        name: 'Invitación a Evento',
        description: 'Plantilla elegante para invitaciones a eventos',
        thumbnail: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDIwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjMkMzRTUwIi8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjE2MCIgaGVpZ2h0PSI2MCIgZmlsbD0iI0ZGQzEwNyIvPgo8cmVjdCB4PSIyMCIgeT0iMTAwIiB3aWR0aD0iMTYwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI0Y4RjlGQSIvPgo8L3N2Zz4K',
        category: 'evento',
        tags: ['evento', 'invitación', 'conferencia'],
        content: [
          {
            id: 'text_1',
            type: 'TextBlock',
            content: '<h1 style="color: #ffc107; text-align: center; background: #2c3e50; padding: 20px; margin: 0;">ESTÁS INVITADO</h1>',
            props: { fontSize: '24px', color: '#ffc107' }
          },
          {
            id: 'text_2',
            type: 'TextBlock',
            content: '<h2 style="text-align: center; color: #2c3e50;">Conferencia Anual de Tecnología</h2><p style="text-align: center; font-size: 18px;">Únete a nosotros para un día lleno de innovación y networking</p>',
            props: { fontSize: '18px', color: '#2c3e50' }
          },
          {
            id: 'table_1',
            type: 'TableBlock',
            content: '',
            props: {
              headers: ['Detalle', 'Información'],
              rows: [
                ['Fecha', '15 de Enero, 2024'],
                ['Hora', '9:00 AM - 6:00 PM'],
                ['Lugar', 'Centro de Convenciones'],
                ['Precio', 'Entrada Gratuita']
              ],
              showHeader: true
            }
          },
          {
            id: 'button_1',
            type: 'ButtonBlock',
            content: {
              text: 'CONFIRMAR ASISTENCIA',
              url: '#',
              styles: {
                backgroundColor: '#2c3e50',
                color: '#ffffff',
                align: 'center'
              }
            },
            props: {}
          }
        ],
        isCustom: false
      }
    ]
  }

  // =================== UTILIDADES ===================

  /**
   * Genera un ID único para plantillas
   */
  generateId() {
    return 'template_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  /**
   * Genera una miniatura básica para la plantilla
   */
  generateThumbnail(content) {
    // Por simplicidad, usamos una imagen placeholder
    // En una implementación real, podríamos generar una miniatura del contenido
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDIwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjRjhGOUZBIi8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjE2MCIgaGVpZ2h0PSI0MCIgZmlsbD0iI0U5RUNFRiIvPgo8cmVjdCB4PSIyMCIgeT0iODAiIHdpZHRoPSIxNjAiIGhlaWdodD0iNDAiIGZpbGw9IiNFOUVDRUYiLz4KPC9zdmc+Cg=='
  }

  /**
   * Filtra plantillas por categoría
   */
  getTemplatesByCategory(category) {
    const allTemplates = this.getAllTemplates()
    const saved = allTemplates.saved.filter(t => t.category === category)
    const predefined = allTemplates.predefined.filter(t => t.category === category)
    
    return {
      saved,
      predefined
    }
  }

  /**
   * Busca plantillas por texto
   */
  searchTemplates(searchTerm) {
    const allTemplates = this.getAllTemplates()
    const term = searchTerm.toLowerCase()
    
    const filterTemplates = (templates) => {
      return templates.filter(template => 
        template.name.toLowerCase().includes(term) ||
        template.description.toLowerCase().includes(term) ||
        template.tags.some(tag => tag.toLowerCase().includes(term))
      )
    }
    
    return {
      saved: filterTemplates(allTemplates.saved),
      predefined: filterTemplates(allTemplates.predefined)
    }
  }

  /**
   * Duplica una plantilla
   */
  duplicateTemplate(templateId, newName = null) {
    const template = this.getTemplateById(templateId)
    if (!template) {
      return {
        success: false,
        message: 'Plantilla no encontrada'
      }
    }

    const duplicatedTemplate = {
      ...template,
      name: newName || `${template.name} (Copia)`,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isCustom: true
    }

    return this.saveTemplate(duplicatedTemplate)
  }

  /**
   * Exporta una plantilla como JSON
   */
  exportTemplate(templateId) {
    const template = this.getTemplateById(templateId)
    if (!template) {
      return {
        success: false,
        message: 'Plantilla no encontrada'
      }
    }

    try {
      const exportData = {
        template,
        exportedAt: new Date().toISOString(),
        version: '1.0'
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${template.name.replace(/[^a-zA-Z0-9]/g, '_')}.json`
      a.click()
      window.URL.revokeObjectURL(url)

      return {
        success: true,
        message: 'Plantilla exportada exitosamente'
      }
    } catch (error) {
      console.error('Error al exportar plantilla:', error)
      return {
        success: false,
        message: 'Error al exportar la plantilla'
      }
    }
  }

  /**
   * Importa una plantilla desde JSON
   */
  importTemplate(fileContent) {
    try {
      const data = JSON.parse(fileContent)
      
      if (!data.template || !data.template.content) {
        return {
          success: false,
          message: 'Archivo de plantilla inválido'
        }
      }

      const template = {
        ...data.template,
        name: `${data.template.name} (Importada)`,
        isCustom: true
      }

      return this.saveTemplate(template)
    } catch (error) {
      console.error('Error al importar plantilla:', error)
      return {
        success: false,
        message: 'Error al procesar el archivo de plantilla'
      }
    }
  }
}

// Exportar como singleton
export default new TemplateService() 